import { ChangeEvent, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useAddAdminUserAPI from './useAddAdminUserAPI';
import useAdminByEmailAPI from './useAdminByEmailAPI';
import {
  AdminGeneralInfo,
  initialAdminDetails,
} from '../../EditAdminUser/hooks/useGetAdminByIdState';
import { isEmailValid, isPasswordStrong } from '../../../utils/Validators';
import { ROUTE_ADMIN } from '../../../components/Routing/routeHelpers';
import { createDynamicApolloClient } from '../../../apollo/getApolloClient';

export enum AdminRole {
  SUPER_ADMIN_ORG = 'SUPER_ADMIN_ORG',
  ORGANIZATION_ADMIN = 'ORGANIZATION_ADMIN',
  ALEF_CONSOLE_SUPPORT = 'ALEF_CONSOLE_SUPPORT',
  ALEF_ORG_SUPPORT = 'ALEF_ORG_SUPPORT',
  EMARKING_ADMIN = 'EMARKING_ADMIN',
}

const initDetails = {
  ...initialAdminDetails,
  role: AdminRole.SUPER_ADMIN_ORG,
};

const useAddAdminUser = () => {
  const history = useHistory();
  const [adminDetails, setAdminDetails] = useState<AdminGeneralInfo>({
    ...initDetails,
  });
  const [adminId, setAdminId] = useState<string | null>(null);
  const [isAdminExist, setIsAdminExist] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const resetAdminDetails = () => {
    setAdminDetails({ ...initDetails });
  };

  const { addAdmin, addOrgAdminByTenant } = useAddAdminUserAPI({
    onCompleted: resetAdminDetails,
    client: createDynamicApolloClient(adminDetails.tenant),
  });

  const { adminByEmail, loading, message } = useAdminByEmailAPI({
    onComplete: (userId, isEmailExist) => {
      if (userId) {
        setAdminId(userId);
        setIsModalOpen(true);
      }
      setIsAdminExist(isEmailExist);
    },
  });

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id: fieldName, value } = e.target;

    if (fieldName === 'email' && !isAdminExist) {
      setIsAdminExist(true);
    }

    const updatedDetails = {
      ...adminDetails,
      [fieldName]: value,
    };

    if (
      fieldName === 'role' &&
      value !== AdminRole.ORGANIZATION_ADMIN &&
      value !== AdminRole.ALEF_ORG_SUPPORT
    ) {
      updatedDetails.tenant = '';
      updatedDetails.organisations = [];
    }

    if (fieldName === 'tenant' && adminDetails.organisations.length > 0) {
      updatedDetails.organisations = [];
    }

    setAdminDetails(updatedDetails);
  };

  const getIsFormInValid = () => {
    const {
      confirmPassword,
      password,
      email,
      role,
      tenant,
      organisations,
      firstName,
      lastName,
    } = adminDetails;
    const areFieldsEmpty = !Object.values(adminDetails).find((v) => v === '');

    if (
      !isEmailValid(email) ||
      firstName.trim() === '' ||
      lastName.trim() === ''
    ) {
      return true;
    }

    if (
      (role === AdminRole.ORGANIZATION_ADMIN ||
        role === AdminRole.ALEF_ORG_SUPPORT) &&
      (tenant === '' || organisations.length === 0)
    ) {
      return true;
    }

    if (password !== '' || confirmPassword !== '') {
      const isPassMismatch = password !== confirmPassword;
      return isPassMismatch || !isPasswordStrong(password);
    }

    return areFieldsEmpty;
  };

  const onAdd = () => {
    if (
      adminDetails.role === AdminRole.ORGANIZATION_ADMIN ||
      adminDetails.role === AdminRole.ALEF_ORG_SUPPORT
    ) {
      addOrgAdminByTenant(adminDetails);
    } else {
      addAdmin(adminDetails);
    }
  };

  const onCancel = () => history.push(ROUTE_ADMIN.adminsPage);

  const onCheckEmail = () => {
    adminByEmail(adminDetails.email);
  };

  return {
    adminId,
    messageText: message,
    adminDetails,
    isAdminExist,
    isLoading: loading,
    isFormInValid: getIsFormInValid(),
    modal: { isOpen: isModalOpen, setIsOpen: setIsModalOpen },
    onCheckEmail,
    onInputChange,
    onAdd,
    onCancel,
  };
};

export default useAddAdminUser;
